<template>
    <div class="relative">
        <div
            class="group flex w-full min-w-0 items-center gap-3 rounded-full border border-transparent bg-dark px-4 py-2 text-primary-lighter shadow-lg transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 hover:border-primary-medium hover:bg-primary-dark"
            :class="containerClass"
        >
            <label :for="id" class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                {{ label }} <span v-if="required" class="text-red-600">*</span> :
            </label>
            <input
                :id="id"
                :value="modelValue"
                @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
                @blur="emit('blur')"
                type="date"
                :min="min"
                :disabled="disabled"
                :aria-describedby="describedByIds || undefined"
                :aria-invalid="showError && !!error"
                :class="inputClass"
            />
        </div>
        <p v-if="showError && error" :id="`${id}-error`" class="mt-1 text-sm text-red-600">{{ error }}</p>
        <p v-if="hint" :id="`${id}-hint`" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

interface Props {
    modelValue: string;
    label: string;
    required?: boolean;
    disabled?: boolean;
    min?: string;
    hint?: string;
    error?: string;
    showError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    required: false,
    disabled: false,
    min: '',
    hint: '',
    error: '',
    showError: false,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'blur'): void;
}>();

const id = ref(`date-${Math.random().toString(36).slice(2, 11)}`);

const containerClass = computed(() => {
    if (!(props.showError && props.error)) {
        return '';
    }

    return 'border-red-500 hover:border-red-500 focus-within:ring-red-400';
});

const inputClass = computed(() => {
    if (!(props.showError && props.error)) {
        return 'w-full min-w-0 flex-1 rounded-xl bg-primary-light p-1 px-3 text-lg font-bold tracking-wide text-dark focus:outline-none disabled:cursor-not-allowed disabled:opacity-60';
    }

    return 'w-full min-w-0 flex-1 rounded-xl bg-red-50 p-1 px-3 text-lg font-bold tracking-wide text-red-700 placeholder:text-red-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60';
});

const describedByIds = computed(() => {
    const ids: string[] = [];
    if (props.showError && props.error) ids.push(`${id.value}-error`);
    if (props.hint) ids.push(`${id.value}-hint`);
    return ids.join(' ');
});
</script>
